import {
  Badge,
  Button,
  createTableColumn,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  type TableColumnDefinition,
  Textarea,
  Toolbar,
  ToolbarButton,
  tokens,
} from "@fluentui/react-components"
import {
  AddRegular,
  ArrowClockwiseRegular,
  DeleteRegular,
  EditRegular,
  KeyMultipleRegular,
  MoreHorizontalRegular,
  SearchRegular,
} from "@fluentui/react-icons"
import type { Page, Scope } from "@rema/shared"
import { SCOPE_KEY_REGEX } from "@rema/shared"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { AppDrawerForm } from "../../components/app-drawer-form"
import { ConfirmDialog } from "../../components/confirm-dialog"
import { DataGridPage } from "../../components/data-grid-page"
import { EmptyState } from "../../components/empty-state"
import { FormField } from "../../components/form-field"
import { PageHeader } from "../../components/page-header"
import { es } from "../../i18n/es"
import { api, isApiError } from "../../lib/api"
import { applyFieldErrors, problemMessage } from "../../lib/problem"
import { scopesI18n as t } from "./i18n"

const SCOPES_QUERY_KEY = ["admin", "scopes"] as const

const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  search: {
    minWidth: "240px",
  },
})

interface ScopeFormValues {
  key: string
  description: string
}

// Catálogo de scopes (M3 · F3.1): DataGrid + Drawer de alta/edición + borrado.
// El alcance (instancia vs dominio) lo decide el servidor por el token; aquí no se
// expone selector de dominio (eso es Ola B en /domains).
export const ScopesPage = () => {
  const styles = useStyles()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState("")
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Scope | null>(null)
  const [toDelete, setToDelete] = useState<Scope | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const { control, handleSubmit, reset, setError } = useForm<ScopeFormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { key: "", description: "" },
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: SCOPES_QUERY_KEY })

  const saveMutation = useMutation({
    mutationFn: async (values: ScopeFormValues) => {
      const description = values.description.trim() || null
      if (editing) {
        return api.patch<Scope>(`/admin/scopes/${editing.id}`, { description })
      }
      return api.post<Scope>("/admin/scopes", {
        key: values.key.trim(),
        description: description ?? undefined,
      })
    },
    onSuccess: () => {
      invalidate()
      closeDrawer()
    },
    onError: (error) => {
      if (isApiError(error)) {
        const mapped = applyFieldErrors(error.problem, setError)
        if (!mapped) setFormError(problemMessage(error.problem))
      } else {
        setFormError(editing ? t.updateError : t.createError)
      }
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (scope: Scope) => api.delete<void>(`/admin/scopes/${scope.id}`),
    onSuccess: () => {
      invalidate()
      setToDelete(null)
    },
  })

  const openCreate = () => {
    setEditing(null)
    setFormError(null)
    reset({ key: "", description: "" })
    setDrawerOpen(true)
  }

  const openEdit = (scope: Scope) => {
    setEditing(scope)
    setFormError(null)
    reset({ key: scope.key, description: scope.description ?? "" })
    setDrawerOpen(true)
  }

  const closeDrawer = () => {
    setDrawerOpen(false)
    setEditing(null)
    setFormError(null)
  }

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    saveMutation.mutate(values)
  })

  const fetchPage = async (cursor: string | null): Promise<Page<Scope>> =>
    api.get<Page<Scope>>("/admin/scopes", {
      query: { limit: 50, cursor: cursor ?? undefined, q: search.trim() || undefined },
    })

  const columns = buildColumns({ onEdit: openEdit, onDelete: setToDelete })

  const toolbar = (
    <Toolbar className={styles.toolbar} aria-label={t.searchLabel}>
      <Input
        className={styles.search}
        value={search}
        onChange={(_, data) => setSearch(data.value)}
        placeholder={t.searchPlaceholder}
        contentBefore={<SearchRegular />}
        aria-label={t.searchLabel}
      />
    </Toolbar>
  )

  return (
    <>
      <PageHeader
        title={es.nav.scopes}
        description={t.pageDescription}
        actions={
          <Toolbar aria-label={t.newScope}>
            <ToolbarButton appearance="primary" icon={<AddRegular />} onClick={openCreate}>
              {t.newScope}
            </ToolbarButton>
            <ToolbarButton
              icon={<ArrowClockwiseRegular />}
              aria-label={t.refresh}
              onClick={() => invalidate()}
            />
          </Toolbar>
        }
      />
      <DataGridPage<Scope>
        queryKey={[...SCOPES_QUERY_KEY, search.trim()]}
        fetchPage={fetchPage}
        columns={columns}
        getRowId={(scope) => scope.id}
        toolbar={toolbar}
        emptyState={
          <EmptyState
            icon={<KeyMultipleRegular />}
            title={t.emptyTitle}
            description={t.emptyDescription}
            action={
              <Button appearance="primary" icon={<AddRegular />} onClick={openCreate}>
                {t.emptyAction}
              </Button>
            }
          />
        }
      />

      <AppDrawerForm
        open={drawerOpen}
        title={editing ? t.editTitle : t.createTitle}
        onSubmit={onSubmit}
        onClose={closeDrawer}
        submitting={saveMutation.isPending}
        error={formError}
      >
        <FormField
          control={control}
          name="key"
          label={t.keyLabel}
          required={!editing}
          hint={editing ? t.keyReadonlyHint : t.keyHint}
          rules={
            editing
              ? undefined
              : {
                  required: t.keyRequired,
                  validate: (value) => SCOPE_KEY_REGEX.test(value.trim()) || t.keyFormat,
                }
          }
        >
          {(field, invalid) => (
            <Input
              {...field}
              type="text"
              autoFocus={!editing}
              disabled={editing !== null}
              placeholder={t.keyPlaceholder}
              aria-invalid={invalid}
            />
          )}
        </FormField>
        <FormField control={control} name="description" label={t.descriptionLabel}>
          {(field) => (
            <Textarea {...field} resize="vertical" placeholder={t.descriptionPlaceholder} />
          )}
        </FormField>
      </AppDrawerForm>

      <ConfirmDialog
        open={toDelete !== null}
        title={t.deleteTitle}
        message={toDelete ? t.deleteMessage(toDelete.key) : ""}
        confirmLabel={t.deleteConfirm}
        danger
        confirming={deleteMutation.isPending}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete)}
        onCancel={() => setToDelete(null)}
      />
    </>
  )
}

interface ColumnHandlers {
  onEdit: (scope: Scope) => void
  onDelete: (scope: Scope) => void
}

function buildColumns({ onEdit, onDelete }: ColumnHandlers): TableColumnDefinition<Scope>[] {
  return [
    createTableColumn<Scope>({
      columnId: "key",
      renderHeaderCell: () => t.colKey,
      renderCell: (scope) => (
        <code style={{ fontFamily: tokens.fontFamilyMonospace }}>{scope.key}</code>
      ),
    }),
    createTableColumn<Scope>({
      columnId: "description",
      renderHeaderCell: () => t.colDescription,
      renderCell: (scope) => scope.description ?? "—",
    }),
    createTableColumn<Scope>({
      columnId: "scope",
      renderHeaderCell: () => t.colScope,
      renderCell: (scope) => (
        <Badge appearance="outline" color={scope.domain_id ? "brand" : "informative"}>
          {scope.domain_id ? t.scopeDomain : t.scopeGlobal}
        </Badge>
      ),
    }),
    createTableColumn<Scope>({
      columnId: "actions",
      renderHeaderCell: () => t.colActions,
      renderCell: (scope) => (
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              aria-label={t.rowActions}
              icon={<MoreHorizontalRegular />}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem icon={<EditRegular />} onClick={() => onEdit(scope)}>
                {t.editScope}
              </MenuItem>
              <MenuItem icon={<DeleteRegular />} onClick={() => onDelete(scope)}>
                {t.deleteScope}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      ),
    }),
  ]
}
